import { apply, first, group_by, map, object_map, scan } from "@pdfking/rfunc"
import { Identity, JSONDecode, RecordMerge } from "@pdfking/tfunc"
import uuid from "@pdfking/workflow/utils/uuid"
import { Autocomplete } from "../comp/Autocomplete"
import { Barcode } from "../comp/Barcode"
import { CartCounter } from "../comp/CartCounter"
import { Collapsable } from "../comp/Collapsable"
import { Double } from "../comp/Double"
import { DoubleAutocomplete } from "../comp/DoubleAutocomplete"
import { Markdown } from "../comp/Markdown"
import { Megamenu } from "../comp/Megamenu"
import { Messages } from "../comp/Messages"
import { Publicsearch } from "../comp/Publicsearch"
import { Rating } from "../comp/Rating"
import { Routerview } from "../comp/Routerview"
import { Scroll } from "../comp/Scroll"
import { Searchselect } from "../comp/Searchselect"
import { Sizedatalist } from "../comp/Sizedatalist"
import { Uploader } from "../comp/Uploader"
import { Username } from "../comp/Username"
import autocomplete_index from "../constants/autocomplete"
import append from "../dom/append"
import element from "../dom/element"
import selector from "../dom/selector"
import register_dom_update from "../navigation/register_dom_update"
import navigate from "../routes/navigate"
import { SLIDESHOW_VIEW } from "../routes/urls"
import user from "../state/user"
import { init, replace } from "./init"

function require_staff(func, func2?) {
  return el => {
    if (user().is_staff) {
      return (func || Identity)(el)
    }
    return (func2 || Identity)(el)
  }
}

const components = {
  autocomplete: el =>
    replace(Autocomplete, el, {
      multiple: el.matches("[multiple]"),
      name: el.name,
      options: {},
      value:
        JSONDecode(el.getAttribute("data-initial") || "null") ||
        (el.value ? [{ id: el.value, unicode: el.value }] : []),
    }),
  autocomplete_select: el =>
    autocomplete_index()
      .load_autocomplete(el.getAttribute("data-autocomplete"))
      .then(content => {
        const options = {}

        scan(
          labels =>
            scan(
              (unicode, id) =>
                (options[id] = {
                  group: labels.name,
                  id,
                  unicode,
                }),
              labels.objects
            ),
          content ? content.objects : []
        )

        return replace(Searchselect, el, {
          name: el.name,
          options,
          placeholder: el.getAttribute("placeholder"),
          value: el.value,
        })
      }),
  barcode: el => init(Barcode, el, { name: el.name, value: el.value }),

  cart_counter: el => init(CartCounter, el),

  collapsable: el =>
    init(Collapsable, el, { content: el.innerHTML, preserve_class: true }),

  double: el =>
    replace(Double, el, {
      name: el.name,
      options: group_by(
        map(
          (el: HTMLOptionElement) =>
            apply(
              (f, s) => ({
                group: s ? f : f,
                label:
                  (el.parentNode as HTMLElement)?.getAttribute("label") || "",
                name: s ? s : "",
                value: el.value,
              }),
              el.innerHTML.split(": ")
            ),
          selector("option", el) as HTMLOptionElement[]
        ),
        info => info.label,
        array =>
          group_by(
            array,
            info => info.group,
            array =>
              object_map(
                v => v.value,
                v => v.name,
                array
              )
          )
      ),
      size: "70%",
      value: el.value,
      disabled: !!el.hasAttribute("disabled"),
    }),

  double_autocomplete: el =>
    replace(DoubleAutocomplete, el, {
      name: el.name,
      value: el.value,
      disabled: !!el.hasAttribute("disabled"),
    }),
  filelist: el =>
    import(/* webpackChunkName: "history" */ "../comp/files/Filelist").then(m =>
      init(m.Filelist, el, {
        filelist: el.getAttribute("data-filelist"),
      })
    ),
  imageload: el => {
    const strip = () => {
      el.removeAttribute("loading")
    }

    if (el.complete) {
      strip()
    } else {
      el.addEventListener("load", strip)
    }
  },
  markdown: require_staff(el =>
    replace(Markdown, el, {
      class: el.getAttribute("class"),
      content: el.innerHTML,
      id: el.id,
      name: el.name,
      placeholder: el.getAttribute("placeholder"),
    })
  ),

  megamenu: el => Megamenu(el),
  publicsearch: el =>
    init(Publicsearch, el, { placeholder: el.getAttribute("placeholder") }),
  rating: el => replace(Rating, el, { value: el.value, name: el.name }),
  report: require_staff(el =>
    import(/* webpackChunkName: "report" */ "../comp/Report").then(m =>
      replace(m.Report, el, {
        report: JSONDecode(first(selector("script", el)).innerHTML),
      })
    )
  ),
  routerview: el => replace(Routerview, el),
  scroll: el => Scroll(el),
  searchselect: el =>
    replace(Searchselect, el, {
      multiple: el.matches("[multiple]"),
      name: el.name,
      options: object_map(
        (opt: HTMLOptionElement) => opt.value,
        (opt: HTMLOptionElement) => ({
          group: (opt.parentNode as HTMLElement)?.getAttribute("label") || null,
          id: opt.value,
          unicode: opt.innerHTML,
        }),
        selector("option", el) as HTMLOptionElement[]
      ),
      value: map(
        (opt: HTMLOptionElement) => opt.value,
        selector("option[selected]", el) as HTMLOptionElement[]
      ),
    }),

  sizedatalist: el => {
    const component = element("div")
    const id = uuid()

    el.setAttribute("list", id)

    append(el.parentNode, component)

    init(
      Sizedatalist,
      component,
      RecordMerge(
        object_map(
          k => k,
          k => JSONDecode(el.getAttribute(`data-${k}`) || "[]"),
          ["units", "min", "max"]
        ),
        {
          autocomplete: el.getAttribute("data-autocomplete"),
          id,
          name: el.name,
        }
      )
    )
  },

  slideshow: el => {
    if (!el.getAttribute("href"))
      el.addEventListener("click", e => {
        e.preventDefault()
        e.stopPropagation()

        navigate(SLIDESHOW_VIEW, {
          selected: el.getAttribute("data-slide-src") || el.getAttribute("src"),
          selector: el.getAttribute("data-selector"),
        })
      })
  },

  spreadsheet: el =>
    import(/* webpackChunkName: "spreadsheet" */ "../comp/Spreadsheet").then(
      m =>
        init(m.Spreadsheet, el, {
          errors: JSONDecode(el.getAttribute("data-errors")),
          input: JSONDecode(el.getAttribute("value") || "[{}]"),
          name: el.getAttribute("name"),
          schema: JSONDecode(el.getAttribute("data-schema")),
        })
    ),

  toggleclass: el => {
    const classname = el.getAttribute("data-class")
    const target = el.getAttribute("data-target")
    el.addEventListener("click", _e =>
      scan(
        e =>
          e.classList.contains(classname)
            ? e.classList.remove(classname)
            : e.classList.add(classname),
        selector(target)
      )
    )
  },
  uploader: async el =>
    replace(Uploader, el, {
      filelist: el.getAttribute("data-filelist"),
      name: el.name,
    }),
  username: el => init(Username, el),
}

init(Messages)

register_dom_update({
  after: () => {
    scan(el => {
      const func = components[el.getAttribute("data-component")]
      if (func) func(el)
      el.removeAttribute("data-component")
    }, selector("[data-component]"))

    scan(components.searchselect, selector("select[multiple]"))
    scan(components.imageload, selector("[loading=lazy]"))
  },
  login: () =>
    user().is_staff
      ? import(/* webpackChunkName: "admin" */ "../comp/files/Filesink").then(
          m => init(m.Filesink)
        )
      : null,
})
